import { useEffect, useRef } from 'react'

const CAPS = [
  {
    num: '01',
    title: 'Visual rule editor',
    desc: 'Risk and compliance teams compose, test and ship new rules in hours, without ever opening an engineering ticket.',
  },
  {
    num: '02',
    title: 'Live interdiction',
    desc: 'Block suspicious payments before they settle. Cut losses at the rail itself, not later inside a recovery queue.',
  },
  {
    num: '03',
    title: 'Typology-level scoring',
    desc: 'Risk signals roll up into named threat scenarios: mule rings, smurfing, account takeover, velocity abuse and more.',
  },
  {
    num: '04',
    title: 'Hub-and-spoke runtime',
    desc: 'Asynchronous messaging on NATS JetStream. Each rule engine scales horizontally and independently under load.',
  },
  {
    num: '05',
    title: 'Audit-grade trails',
    desc: 'Every verdict captured with its full reasoning chain. Regulator-ready exports available on demand.',
  },
  {
    num: '06',
    title: 'Native ISO 20022',
    desc: 'Built around pacs.008, pacs.002 and modern payment messaging. REST APIs included out of the box.',
  },
  {
    num: '07',
    title: 'Behavioral mule detection',
    upcoming: true,
    desc: 'Bayesian scoring across fan-in, fan-out and pass-through signals. Plus multi-hop graph cycle detection. Catches mule accounts that fuzzy name matching alone cannot.',
  },
]

export default function CoreCapabilities() {
  const gridRef = useRef(null)

  useEffect(() => {
    const grid = gridRef.current
    if (!grid) return
    const cards = grid.querySelectorAll('.sf-caps3-item')
    const cleanups = []
    cards.forEach((card) => {
      let tx = 50, ty = 50, cx = 50, cy = 50, raf = null
      const tick = () => {
        cx += (tx - cx) * 0.18
        cy += (ty - cy) * 0.18
        card.style.setProperty('--mx', cx.toFixed(2) + '%')
        card.style.setProperty('--my', cy.toFixed(2) + '%')
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
          raf = requestAnimationFrame(tick)
        } else { raf = null }
      }
      const onMove = (e) => {
        const r = card.getBoundingClientRect()
        tx = ((e.clientX - r.left) / r.width) * 100
        ty = ((e.clientY - r.top) / r.height) * 100
        if (raf === null) raf = requestAnimationFrame(tick)
      }
      const onLeave = () => {
        tx = 50; ty = 50
        if (raf === null) raf = requestAnimationFrame(tick)
      }
      card.addEventListener('pointermove', onMove, { passive: true })
      card.addEventListener('pointerleave', onLeave)
      cleanups.push(() => {
        card.removeEventListener('pointermove', onMove)
        card.removeEventListener('pointerleave', onLeave)
        if (raf !== null) cancelAnimationFrame(raf)
      })
    })
    return () => cleanups.forEach((fn) => fn())
  }, [])

  return (
    <section className="sf-caps3" id="capabilities">
      <div className="sf-container">
        <div className="sf-section-header sf-reveal" data-sf-reveal>
          <span className="sf-eyebrow"><span className="sf-pulse" />The toolkit</span>
          <h2 className="sf-section-title">
            Everything risk and compliance teams reach for, <em>in one place.</em>
          </h2>
          <p className="sf-section-lede">
            Live decisioning, rules a compliance lead can ship without filing a ticket,
            and the audit trail every regulator expects.
          </p>
        </div>
        <div className="sf-caps3-grid" ref={gridRef}>
          {CAPS.map((c, i) => (
            <div
              key={c.num}
              className={`sf-caps3-item sf-reveal${i % 3 ? ` sf-reveal-d${i % 3}` : ''}`}
              data-sf-reveal
            >
              <span className="sf-caps3-num">{c.num}</span>
              {c.upcoming && (
                <div className="sf-caps3-upcoming-wrap">
                  <span className="sf-caps3-upcoming">Upcoming</span>
                </div>
              )}
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .sf-caps3 {
          background: var(--bg-2);
          padding: clamp(96px,11vw,140px) 0;
          position: relative;
        }
        .sf-caps3::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(60% 40% at 50% 0%,rgba(14,165,233,0.04),transparent 70%);
          pointer-events: none;
        }
        .sf-caps3-grid {
          display: grid; grid-template-columns: repeat(3,1fr);
          gap: 56px 48px;
          max-width: 1180px; margin: 56px auto 0;
          padding: 0 var(--gutter);
          position: relative;
          perspective: 1500px;
        }
        @media (min-width: 961px) {
          .sf-caps3-grid > .sf-caps3-item:last-child:nth-child(3n+1) { grid-column: 2; }
        }
        @media (max-width: 960px) {
          .sf-caps3-grid { grid-template-columns: repeat(2,1fr); gap: 48px 36px; }
        }
        @media (min-width: 561px) and (max-width: 960px) {
          .sf-caps3-grid > .sf-caps3-item:last-child:nth-child(2n+1) {
            grid-column: 1 / -1; justify-self: center;
            max-width: 540px; width: 100%;
          }
        }
        @media (max-width: 560px) {
          .sf-caps3-grid { grid-template-columns: 1fr; gap: 36px; }
        }
        .sf-caps3-item {
          --mx: 50%; --my: 50%;
          --sb-color: 14,165,233;
          position: relative; isolation: isolate;
          padding: 26px 26px 28px;
          background: #fff; border-radius: 14px;
          border: 1px solid var(--line);
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: box-shadow 0.3s var(--ease);
          overflow: hidden;
        }
        .sf-caps3-item:nth-child(3n+2) { --sb-color: 34,197,94; }
        .sf-caps3-item::before {
          content: ''; position: absolute; inset: 0;
          border-radius: inherit; padding: 5px;
          background: radial-gradient(180px 180px at var(--mx) var(--my),
            rgba(var(--sb-color),0.60),
            rgba(var(--sb-color),0.22) 40%,
            transparent 70%);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          filter: blur(1.5px);
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.5s var(--ease);
          z-index: 0;
        }
        .sf-caps3-item:hover::before { opacity: 1; }
        .sf-caps3-item > * { position: relative; z-index: 1; }

        .sf-caps3-item.sf-reveal {
          opacity: 0;
          transform: translateY(48px);
          transition: none;
        }
        .sf-caps3-item.sf-reveal.sf-in {
          animation: sfCapsRiseFlip 1.3s cubic-bezier(0.4,0,0.2,1) forwards;
        }
        .sf-caps3-item.sf-reveal-d1.sf-reveal.sf-in { animation-delay: 0.18s; }
        .sf-caps3-item.sf-reveal-d2.sf-reveal.sf-in { animation-delay: 0.36s; }
        @keyframes sfCapsRiseFlip {
          0%   { opacity: 0; transform: translateY(48px) rotateY(180deg); }
          28%  { opacity: 1; transform: translateY(0)    rotateY(180deg); }
          100% { opacity: 1; transform: translateY(0)    rotateY(0deg); }
        }
        .sf-caps3-item > .sf-caps3-num,
        .sf-caps3-item > .sf-caps3-upcoming-wrap,
        .sf-caps3-item > h3,
        .sf-caps3-item > p {
          opacity: 0;
          transition: opacity 0.45s var(--ease);
        }
        .sf-caps3-item.sf-in > .sf-caps3-num,
        .sf-caps3-item.sf-in > .sf-caps3-upcoming-wrap,
        .sf-caps3-item.sf-in > h3,
        .sf-caps3-item.sf-in > p {
          opacity: 1;
          transition-delay: 1.35s;
        }
        .sf-caps3-item.sf-reveal-d1.sf-in > .sf-caps3-num,
        .sf-caps3-item.sf-reveal-d1.sf-in > .sf-caps3-upcoming-wrap,
        .sf-caps3-item.sf-reveal-d1.sf-in > h3,
        .sf-caps3-item.sf-reveal-d1.sf-in > p {
          transition-delay: 1.53s;
        }
        .sf-caps3-item.sf-reveal-d2.sf-in > .sf-caps3-num,
        .sf-caps3-item.sf-reveal-d2.sf-in > .sf-caps3-upcoming-wrap,
        .sf-caps3-item.sf-reveal-d2.sf-in > h3,
        .sf-caps3-item.sf-reveal-d2.sf-in > p {
          transition-delay: 1.71s;
        }

        .sf-caps3-num {
          display: inline-block; font-family: var(--mono);
          font-size: 15px; letter-spacing: 0.22em;
          color: var(--ca-700); font-weight: 700; margin-top: 4px;
          transition: transform 0.35s var(--ease),color 0.3s, opacity 0.45s var(--ease);
        }
        .sf-caps3-item:nth-child(3n+2) .sf-caps3-num { color: var(--sf-700); }
        .sf-caps3-item:hover .sf-caps3-num { color: var(--ca-500); }
        .sf-caps3-item:nth-child(3n+2):hover .sf-caps3-num { color: var(--sf-600); }
        .sf-caps3-item h3 {
          margin-top: 10px; font-size: 19px; font-weight: 600;
          letter-spacing: -0.01em; color: var(--text); line-height: 1.25;
        }
        .sf-caps3-upcoming-wrap { margin-top: 10px; }
        .sf-caps3-upcoming {
          display: inline-flex; align-items: center;
          font-family: var(--mono); font-size: 12px;
          font-weight: 700; letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--ca-700); background: var(--ca-50);
          border: 1px solid var(--ca-200);
          padding: 6px 12px; border-radius: 999px; line-height: 1;
        }
        .sf-caps3-item p {
          margin-top: 10px; font-size: 14.5px;
          color: var(--text-2); line-height: 1.65;
        }
        @media (prefers-reduced-motion: reduce) {
          .sf-caps3-item.sf-reveal { opacity: 1; transform: none; }
          .sf-caps3-item.sf-reveal.sf-in { animation: none; }
          .sf-caps3-item > .sf-caps3-num,
          .sf-caps3-item > .sf-caps3-upcoming-wrap,
          .sf-caps3-item > h3,
          .sf-caps3-item > p { opacity: 1; }
        }
      `}</style>
    </section>
  )
}
